import { Shape } from './shape.interface';
import { FieldMapContext } from '../field-map-context.model';
import { deepCloneLatLngs, updateStyle } from './shape-utils';
import * as L from 'leaflet';

export abstract class FieldMapShape implements Shape {

    fieldMapContext: FieldMapContext;
    map: L.Map;
    type: 'FIELD' | 'BLOCK' | 'STUDY' | 'PLOT';
    id: string;
    name: string;
    polygon: L.Polygon | null;
    isEditing: boolean;
    isSelected: boolean;
    originalLatLng?: any;
    editInitialLatLng?: L.LatLng[][];
    isOutsideBoundary?: boolean;
    isVisible = true;

    labelMarker: L.Marker | null = null;

    constructor(map: L.Map) {
        this.map = map;
    }

    initialize() {
        // Initialize transform and dragging handlers
        this.polygon.transform = new L.Handler.PathTransform(this.polygon);
        this.polygon.dragging = new L.Handler.PathDrag(this.polygon);
        this.polygon.disableEdit();
        this.addListeners();
        this.updateStyle('DEFAULT');
    }

    draw(): void {
        // Implement
    }

    updateStyle(styleName: 'DEFAULT' | 'HOVER' | 'EDIT' | 'SELECTED' | 'SELECTED_HOVER' | 'BOUNDARY' | 'BOUNDARY_ERROR'): void {
        updateStyle(this, styleName);
    };

    select(): void {
        this.isSelected = true;
        this.updateStyle('SELECTED');
        this.fieldMapContext.shapeSelected(this);
    }

    unSelect(): void {
        this.isSelected = false;
        this.updateStyle('DEFAULT');
        this.fieldMapContext.shapeUnSelected(this);
    }

    async commit(): Promise<void> {
        return this.fieldMapContext.beforeShapeCommit(this).then(() => {
            this.fieldMapContext.enableMouseEventListeners();
            // Important!!! Make sure the transform handler is called after it is enabled.
            this.polygon.transform.reset();
            this.polygon.disableEdit();
            this.polygon.transform.disable();
            this.polygon.dragging.disable();
            this.isEditing = false;
            this.originalLatLng = null;
            if (this.updateStyle) {
                this.updateStyle('DEFAULT');
            }

            this.fieldMapContext.afterShapeCommit(this);

            return Promise.resolve();
        }, () => {
            return Promise.reject();
        });
    }

    reset(): void {
        this.fieldMapContext.enableMouseEventListeners();
        // originalLatLng is populated when transformation is started (edit, rotate, resize)
        // If it has value, revert the polygon to its original size
        if (this.originalLatLng) {
            this.polygon.setLatLngs(this.originalLatLng);
            this.polygon.redraw();
        }
        this.resetEdit();
        // Remove the originalLatLng value
        this.originalLatLng = null;

    }

    edit(): void {
        this.fieldMapContext.disableMouseEventListeners();
        this.storeOriginalLatLng();
        this.resetEdit();
        this.isEditing = true;
        this.updateStyle('EDIT'); // Highlight selected polygon
        this.polygon.enableEdit();
    }

    resize(): void {
        this.fieldMapContext.disableMouseEventListeners();
        this.storeOriginalLatLng();
        this.resetEdit();
        this.isEditing = true;
        this.updateStyle('EDIT');
        this.polygon.transform.enable({ rotation: false, scaling: true });
        // Important!!! Make sure the transform handler is called after it is enabled.
        this.polygon.transform.reset();
        this.polygon.dragging.enable();
    }

    rotate(): void {
        this.fieldMapContext.disableMouseEventListeners();
        this.storeOriginalLatLng();
        this.resetEdit();
        this.isEditing = true;
        this.updateStyle('EDIT');
        this.polygon.transform.enable({ rotation: true, scaling: false });
        // Important!!! Make sure the transform handler is called after it is enabled.
        this.polygon.transform.reset();
        this.polygon.dragging.enable();
    }

    abstract delete(): void;

    removeListeners() {
        this.polygon.off('mouseover');
        this.polygon.off('mouseout');
        this.polygon.off('click');
    }

    addListeners() {
        this.removeListeners();
        this.polygon.on('mouseover', () => {
            if (!this.fieldMapContext.mouseEventListenerEnabled) {
                return;
            }
            if (!this.isEditing && !this.isSelected) {
                this.updateStyle('HOVER');
            }
            if (!this.isEditing && this.isSelected) {
                this.updateStyle('SELECTED_HOVER');
            }
            this.createLabelMarker();
            this.fieldMapContext.shapeMousOver(this);
        });
        this.polygon.on('mouseout', () => {
            if (!this.fieldMapContext.mouseEventListenerEnabled) {
                return;
            }
            if (!this.isEditing && !this.isSelected) {
                this.updateStyle('DEFAULT');
            }
            if (!this.isEditing && this.isSelected) {
                this.updateStyle('SELECTED');
            }
            this.removeLabelMarker();
            this.fieldMapContext.shapeMouseOut(this);
        });
        this.polygon.on('click', (event) => {
            L.DomEvent.stopPropagation(event);
            if (!this.fieldMapContext.mouseEventListenerEnabled) {
                return;
            }
            if (this.isSelected && !this.isEditing) {
                this.unSelect();
            } else if (!this.isEditing) {
                // Select this shape
                this.select();
            }
        });
    }

    resetEdit() {
        // Important!!! Make sure the transform handler is called after it is enabled.
        this.polygon.transform.reset();
        this.polygon.disableEdit();
        this.polygon.transform.disable();
        this.polygon.dragging.disable();
        this.isEditing = false;
        if (this.updateStyle) {
            this.updateStyle('DEFAULT');
        }
    }

    private storeOriginalLatLng() {
        if (!this.originalLatLng) {
            // Store the original coords of the polygon prior to transformation.
            this.originalLatLng = deepCloneLatLngs(this.polygon.getLatLngs());
        }
    }

    private createLabelMarker() {
        if (this.labelMarker) {
            this.removeLabelMarker();
        }

        // Calculate center of polygon
        const bounds = this.polygon.getBounds();
        const northLat = bounds.getNorth();
        const latOffset = (bounds.getNorth() - bounds.getSouth()) * 0.15; // 10% of height offset

        const topPosition = L.latLng(
            northLat + latOffset, // Add offset to move label higher
            bounds.getWest() + (bounds.getEast() - bounds.getWest()) / 2
        );

        // Create custom icon for label
        const labelIcon = L.divIcon({
            className: 'shape-label',
            html: `<div style="
                    background: white;
                    padding: 3px 8px;
                    font-family: Arial, sans-serif;
                    font-size: 12px;
                    color: black;
                    white-space: nowrap;
                    pointer-events: none;
                    transform: translate(-50%, -50%);
                    min-width: 20px;
                    text-align: center;
                    border-radius: 4px;
                   "><span class="badge text-bg-secondary">${this.type}</span> ${this.name}</div>`,
            iconSize: null
        });

        // Create and add marker
        this.labelMarker = L.marker(topPosition, {
            icon: labelIcon,
            zIndexOffset: 1000, // Ensure label appears above polygon
        }).addTo(this.map);
    }

    private removeLabelMarker() {
        if (this.labelMarker) {
            this.map.removeLayer(this.labelMarker);
            this.labelMarker = null;
        }
    }

    abstract isEditable(): boolean

    abstract isResizable(): boolean;

    abstract isRotatable(): boolean;

    abstract isSaveable(): boolean;

    abstract isDeletable(): boolean;

    fitView(): void {
        this.map.fitBounds(this.polygon.getBounds());
    }

    hide(): void {
        // do nothing
    }

    show(): void {
        // do nothing
    }

}
